import React from 'react';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import moment from 'moment';
import FormattedCurrency from '@/shared/components/FormattedCurrency';
import RelationName from '@/shared/components/RelationName';
import NavigateToState from '@/shared/components/NavigateToState';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';
import messages from '../reportingMessages';

const HoursColumn = ({ value }) => (
  <span className={classnames('text-right hours', { 'text-muted': !value })}>
    {(value || 0).toTime()}
  </span>
);

const AmountColumn = ({ value }) => (
  <span className={classnames('text-right amount', { 'text-muted': !value })}>
    <FormattedCurrency value={value || 0} />
  </span>
);

const HoursColumnWithNavigation = ({ value, stateName, navigationProps }) => (
  <span className={classnames('text-right hours', { 'text-muted': !value })}>
    <NavigateToState stateName={stateName} useUrl paramsToJson {...navigationProps}>
      {(value || 0).toTime()}
    </NavigateToState>
  </span>
);

const AmountColumnWithNavigation = ({ value, stateName, navigationProps }) => (
  <span className={classnames('text-right amount', { 'text-muted': !value })}>
    <NavigateToState stateName={stateName} useUrl paramsToJson {...navigationProps}>
      <FormattedCurrency value={value || 0} />
    </NavigateToState>
  </span>
);

const buildRelation = row => {
  const now = moment.utc();
  return {
    id: row.relationId,
    type: row.relationType,
    name: row.relationDisplayName,
    alias: row.relationAlias,
    isActive:
      !row.relationEndDate || (row.relationEndDate && moment.utc(row.relationEndDate).isAfter(now)),
    relationHandlingLevel: row.relationHandlingLevel,
    relationHandlingLevelClarification: row.relationHandlingLevelClarification,
  };
};

const getDateParams = reportFilters => {
  const dateFrom = reportFilters.dateFrom ? moment(reportFilters.dateFrom).toDate() : null;
  const dateUntil = reportFilters.dateUntil ? moment(reportFilters.dateUntil).toDate() : null;
  const referenceYear = reportFilters.referenceYear || null;
  return { dateFrom, dateUntil, referenceYear };
};

// RegistrationInvoiceStatus values matching the SQL aggregation:
// 0 = NotInvoiced, 1 = DraftInvoice, 2 = Invoiced, 3 = DraftLocked, 4 = WrittenOffInternally.
// Internally written-off registrations contribute their sales value to the billable columns
// (with an offsetting correction), so the drill-down must include them too.
// Excluded: -1 (NeverInvoice), since the SQL filters it out.
const billableInvoiceStatuses = [0, 1, 2, 3, 4];

const getBillableHoursNavProps = (row, reportFilters) => {
  const relation = buildRelation(row);
  const { dateFrom, dateUntil, referenceYear } = getDateParams(reportFilters);
  return {
    stateParams: {
      relationId: row.relationId,
      relation,
      finalInvoiceStreamId: row.invoiceStreamId,
      period: 'all',
      dateFrom,
      dateUntil,
      referenceYear,
      filters: {
        invoiceStatus: { items: billableInvoiceStatuses },
        type: { items: [0] },
        isBillable: { items: [true] },
      },
    },
  };
};

const getBillableCostsNavProps = (row, reportFilters) => {
  const relation = buildRelation(row);
  const { dateFrom, dateUntil, referenceYear } = getDateParams(reportFilters);
  return {
    stateParams: {
      relationId: row.relationId,
      relation,
      finalInvoiceStreamId: row.invoiceStreamId,
      period: 'all',
      dateFrom,
      dateUntil,
      referenceYear,
      filters: {
        invoiceStatus: { items: billableInvoiceStatuses },
        type: { items: [1, 2] },
        isBillable: { items: [true] },
      },
    },
  };
};

export const getColumns = (intl, showAssignments, totals, reportFilters) => {
  const columns = [
    {
      Header: <FormattedMessage {...messages.Relation} />,
      Footer: <FormattedMessage {...messages.Totals} />,
      id: 'relation',
      accessor: originalRow => originalRow,
      Cell: ({ row: { original } }) => {
        const relation = buildRelation(original);
        return <RelationName relation={relation} />;
      },
      isVisible: true,
      width: 2,
      minWidth: 100,
    },
    {
      Header: <FormattedMessage {...messages.Assignment} />,
      id: 'assignment',
      accessor: originalRow => mapInvoiceStreamName(originalRow, intl, ''),
      isVisible: showAssignments,
    },
    {
      Header: <FormattedMessage {...messages.BillableHours} />,
      id: 'billableHours',
      Cell: ({ row: { original } }) => {
        const navProps = getBillableHoursNavProps(original, reportFilters);
        return (
          <HoursColumnWithNavigation
            value={original.billableHours}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () => HoursColumn({ value: totals.billableHours }),
      isVisible: true,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.BillableHoursValue} />,
      id: 'billableHoursAmount',
      Cell: ({ row: { original } }) => {
        const navProps = getBillableHoursNavProps(original, reportFilters);
        return (
          <AmountColumnWithNavigation
            value={original.billableHoursAmount}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () => AmountColumn({ value: totals.billableHoursAmount }),
      isVisible: true,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.CorrectionHours} />,
      id: 'correctionHours',
      Cell: ({ row: { original } }) => <HoursColumn value={original.correctionHours} />,
      Footer: () => HoursColumn({ value: totals.correctionHours }),
      isVisible: true,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.CorrectionHoursValue} />,
      id: 'correctionHoursAmount',
      Cell: ({ row: { original } }) => <AmountColumn value={original.correctionHoursAmount} />,
      Footer: () => AmountColumn({ value: totals.correctionHoursAmount }),
      isVisible: true,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.BillableCostsValue} />,
      id: 'billableCostsAmount',
      Cell: ({ row: { original } }) => {
        const navProps = getBillableCostsNavProps(original, reportFilters);
        return (
          <AmountColumnWithNavigation
            value={original.billableCostsAmount}
            stateName="registrations.overview"
            navigationProps={navProps}
          />
        );
      },
      Footer: () => AmountColumn({ value: totals.billableCostsAmount }),
      isVisible: true,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.Invoiced} />,
      id: 'invoicedAmount',
      Cell: ({ row: { original } }) => <AmountColumn value={original.invoicedAmount} />,
      Footer: () => AmountColumn({ value: totals.invoicedAmount }),
      isVisible: true,
      align: 'right',
    },
    {
      Header: <FormattedMessage {...messages.Balance} />,
      id: 'balance',
      Cell: ({ row: { original } }) => <AmountColumn value={original.balance} />,
      Footer: () => AmountColumn({ value: totals.balance }),
      isVisible: true,
      align: 'right',
    },
  ];

  return columns
    .map(x => {
      if (typeof x.isVisible !== 'undefined' && !x.isVisible) return null;
      return x;
    })
    .filter(Boolean);
};

export const defaultColumn = {
  minWidth: 30,
  width: 1,
  maxWidth: 200,
};
